/**
 * The one place a navigation request is decided. Every setup and mapping
 * control hands its destination and its commit action here; nothing navigates
 * on its own.
 *
 * The hook owns no feature state: whether the current screen is dirty, how to
 * save it, how to discard it, and whether a verified baseline exists are all
 * injected, so the guard works the same for setup screens and the mapping
 * workspace.
 */

import * as React from 'react';
import {
  resolveUnsavedChoiceOutcome,
  resolveUnsavedNavigationCopy,
  type NavigationIntent,
  type NavigationOutcome,
  type NavigationRequest,
} from '../../lib/setupRevision';
import { usePendingNavigationIntent } from '../../stores/useUIStore';
import { useUnsavedChangesDialog, type UnsavedChangesCommit } from '../dialogs';
import { registerNavigationGuard, releaseNavigationGuard } from './navigationGuardService';
import { runNavigationTransaction, type RequestNavigation } from './navigationTransaction';

export type { NavigationRequest };

export interface NavigationGuardDependencies {
  /** Whether the screen the user is leaving holds unsaved edits. */
  isDirty: boolean;
  /** Whether a verified snapshot exists that Discard could restore. */
  hasVerifiedBaseline: boolean;
  /** Persists the pending edits; resolves false when the save did not go through. */
  onSave: () => Promise<boolean>;
  /** Restores the last verified baseline; resolves false when it could not be restored. */
  onDiscard: () => Promise<boolean>;
}

export interface NavigationGuardApi {
  requestNavigation: RequestNavigation;
  navigationGuardDialog: React.ReactNode;
  /** True while a request is waiting on the user or on a save/discard operation. */
  isNavigationPending: boolean;
}

export function useNavigationGuard({
  isDirty,
  hasVerifiedBaseline,
  onSave,
  onDiscard,
}: NavigationGuardDependencies): NavigationGuardApi {
  const { runChoice, unsavedChangesDialog } = useUnsavedChangesDialog();
  const pendingNavigationIntent = usePendingNavigationIntent();

  /** Carries out the answer the user gave; the dialog names the phase meanwhile. */
  const commitUnsavedChoice = React.useCallback(
    async (choice: UnsavedChangesCommit): Promise<NavigationOutcome> => {
      // Discarding without a baseline would leave the user with neither their
      // edits nor a known-good setup, so it never runs.
      if (choice === 'discard' && !hasVerifiedBaseline) {
        return 'noVerifiedBaseline';
      }

      const operationSucceeded = choice === 'save' ? await onSave() : await onDiscard();
      return resolveUnsavedChoiceOutcome(choice, operationSucceeded);
    },
    [hasVerifiedBaseline, onDiscard, onSave]
  );

  /** Asks the user what to do with the unsaved edits and reports the result. */
  const resolveUnsavedNavigation = React.useCallback(
    async (intent: NavigationIntent): Promise<NavigationOutcome> => {
      const { choice, result } = await runChoice(
        resolveUnsavedNavigationCopy(intent),
        commitUnsavedChoice
      );

      return choice === 'cancel' ? 'cancelled' : result!;
    },
    [commitUnsavedChoice, runChoice]
  );

  const requestNavigation = React.useCallback<RequestNavigation>(
    (request, commit, options) =>
      runNavigationTransaction(
        request,
        commit,
        { isDirty, resolveUnsaved: resolveUnsavedNavigation },
        options
      ),
    [isDirty, resolveUnsavedNavigation]
  );

  // Controls that live outside this hook's subtree reach the guard through the
  // registry. The ref keeps the registration stable while the dependencies of
  // requestNavigation change underneath it.
  const latestRequestNavigation = React.useRef(requestNavigation);
  latestRequestNavigation.current = requestNavigation;

  React.useEffect(() => {
    const dispatch: RequestNavigation = (request, commit, options) =>
      latestRequestNavigation.current(request, commit, options);
    const token = registerNavigationGuard(dispatch);
    return () => {
      releaseNavigationGuard(token);
    };
  }, []);

  return {
    requestNavigation,
    navigationGuardDialog: unsavedChangesDialog,
    isNavigationPending: Boolean(pendingNavigationIntent),
  };
}
